/**
 * SparklineWithPeriod Component
 * 
 * Sparkline chart with period toggle (1D, 7D, 1M, 1Y).
 * Handles data fetching and period selection.
 */

import { useState, useEffect, useRef } from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { SparklineChart, type SparklinePeriod } from './SparklineChart';

export type SparklinePeriodType = '1H' | '12H' | '1D' | '7D' | '1M' | '1Y';
import { tickerApi } from '../services/api';
import type { TickerHistoryDataPoint } from '../types';

interface SparklineWithPeriodProps {
  ticker: string;
  defaultPeriod?: SparklinePeriod | SparklinePeriodType;
  height?: number;
  width?: number;
  showPeriodToggle?: boolean;
  purchaseDate?: string; // YYYY-MM-DD format for buy time indicator
}

export function SparklineWithPeriod({
  ticker,
  defaultPeriod = '1M',
  height = 40,
  width = 120,
  showPeriodToggle = false,
  purchaseDate,
}: SparklineWithPeriodProps) {
  const [period, setPeriod] = useState<SparklinePeriod | SparklinePeriodType>(defaultPeriod as SparklinePeriod | SparklinePeriodType);
  const [data, setData] = useState<TickerHistoryDataPoint[]>([]);
  const [isLoading, setIsLoading] = useState(false); // Start as false - load after paint
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasLoadedRef = useRef(false);

  // Sync period when defaultPeriod prop changes (from parent)
  useEffect(() => {
    if (defaultPeriod !== period) {
      setPeriod(defaultPeriod);
      hasLoadedRef.current = false; // Allow reload for new period
    }
  }, [defaultPeriod]);

  // Load data asynchronously after initial paint using IntersectionObserver or delayed load
  useEffect(() => {
    if (!ticker) return;

    let cancelled = false;
    let observer: IntersectionObserver | null = null;
    let timeoutId: NodeJS.Timeout | null = null;

    const fetchData = async () => {
      if (cancelled) return;
      
      setIsLoading(true);
      setError(null);

      try {
        const response = await tickerApi.getHistory(ticker, period);
        if (!cancelled) {
          setData(response.data || []);
          setIsLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          console.error(`Failed to fetch sparkline data for ${ticker}:`, err);
          setError('Failed to load');
          setData([]);
          setIsLoading(false);
        }
      }
    };

    // For initial load, use IntersectionObserver or delayed load
    // For period changes, load immediately (user already interacted)
    const shouldDelayLoad = !hasLoadedRef.current;

    if (shouldDelayLoad) {
      // Use IntersectionObserver if available, otherwise use delayed load
      if (typeof window !== 'undefined' && 'IntersectionObserver' in window && containerRef.current) {
        observer = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting) {
              hasLoadedRef.current = true;
              fetchData();
              observer?.disconnect();
            }
          },
          { rootMargin: '100px' } // Start loading 100px before visible
        );
        observer.observe(containerRef.current);
      } else {
        // Fallback: use requestIdleCallback or setTimeout to delay load
        const scheduleLoad = () => {
          if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
            (window as any).requestIdleCallback(() => {
              hasLoadedRef.current = true;
              fetchData();
            }, { timeout: 200 });
          } else {
            timeoutId = setTimeout(() => {
              hasLoadedRef.current = true;
              fetchData();
            }, 200);
          }
        };
        scheduleLoad();
      }
    } else {
      // Period changed - load immediately
      fetchData();
    }

    return () => {
      cancelled = true;
      observer?.disconnect();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [ticker, period]);

  const handlePeriodChange = (
    _event: React.MouseEvent<HTMLElement>,
    newPeriod: SparklinePeriod | SparklinePeriodType | null
  ) => {
    if (newPeriod !== null) {
      setPeriod(newPeriod);
    }
  };

  // Determine color based on trend
  const color =
    data.length > 0
      ? data[data.length - 1].close >= data[0].close
        ? 'up'
        : 'down'
      : 'neutral';

  return (
    <Box
      ref={containerRef}
      sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
    >
      {showPeriodToggle ? (
        <ToggleButtonGroup
          value={period}
          exclusive
          onChange={handlePeriodChange}
          size="small"
          sx={{ width: 'fit-content' }}
        >
          <ToggleButton value="1H" size="small">
            1H
          </ToggleButton>
          <ToggleButton value="12H" size="small">
            12H
          </ToggleButton>
          <ToggleButton value="1D" size="small">
            1D
          </ToggleButton>
          <ToggleButton value="7D" size="small">
            7D
          </ToggleButton>
          <ToggleButton value="1M" size="small">
            1M
          </ToggleButton>
          <ToggleButton value="1Y" size="small">
            1Y
          </ToggleButton>
        </ToggleButtonGroup>
      ) : (
        <Tooltip title={`${period} view`} arrow>
          <Box sx={{ fontSize: '0.7rem', color: 'text.secondary', textAlign: 'center' }}>
            {period}
          </Box>
        </Tooltip>
      )}
      <SparklineChart
        data={data}
        period={period as SparklinePeriod}
        height={height}
        width={width}
        color={color}
        isLoading={isLoading}
        purchaseDate={purchaseDate}
      />
      {error && (
        <Box sx={{ fontSize: '0.7rem', color: 'error.main', textAlign: 'center' }}>
          {error}
        </Box>
      )}
    </Box>
  );
}

